import {
  Col,
  DatePicker,
  Form,
  Input,
  Popover,
  Row,
  Select,
  Switch,
  Table,
} from "antd";
import {
  CloseOutlined,
  SearchOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import COLORS from "common/Colors";
import { APPROVAL_FILTER_TYPES, MONTH_LIST } from "common/Constants";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { dateInDetail, getKenyanDateTime } from "utils/Date";
import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { Info } from "@mui/icons-material";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useSelector } from "react-redux";
import { getUserInfo, getUserList } from "redux/selectors";

dayjs.extend(localeData);
dayjs.extend(weekday);
dayjs.extend(advancedFormat);

const ActionWrapper = styled.div`
  opacity: 0;
  transition: opacity 0.3s ease;
  display: flex;
  gap: 8px;
  justify-content: flex-end;

  /* Show on row hover */
  .ant-table-row:hover & {
    opacity: 1;
  }
`;

/* Remove button — only shown for General level rows */
const RemoveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  color: #2d53da;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 2px 0;
  white-space: nowrap;
  &:hover {
    color: #0958d9;
  }
`;

const AllAnnouncementWrap = styled.div`
  .ant-input::placeholder,
  .ant-picker-input > input::placeholder {
    color: #9ca3af !important;
  }

  .ant-select-selection-placeholder {
    color: #9ca3af !important;
  }

  .ant-input,
  .ant-picker,
  .ant-select-selector,
  .ant-btn {
    border-radius: 24px !important;
  }

  .ant-btn {
    width: 100%;
  }
`;

const StyledTable = styled(Table)`
  .ant-table {
    border: 1px solid #ccc;
    border-radius: 12px;
    overflow: hidden;
  }

  .ant-table-container {
    border-radius: 12px;
  }

  .ant-table-content {
    overflow-x: auto;
  }

  table {
    width: 100%;
    min-width: 900px;
  }

  .ant-table-cell {
    padding: 16px;
    white-space: nowrap;
  }

  .ant-table-thead .ant-table-cell {
    font-weight: 600;
    color: ${COLORS.SECONDARY_BLACK};
  }

  .reason-column {
    min-width: 280px;
    white-space: normal;
  }

  .non-white {
    background: #f9f9f9;
  }

  @media (max-width: 768px) {
    .ant-table-cell {
      padding: 10px;
      font-size: 12px;
    }
  }
  .ant-table-tbody .non-white .ant-table-cell-fix-right {
    background: #f9f9f9 !important;
  }

  .ant-table-tbody tr:not(.non-white) .ant-table-cell-fix-right {
    background: #fff !important;
  }
  .ant-table-tbody .ant-table-row:hover .ant-table-cell-fix-right,
  .ant-table-tbody .ant-table-row:hover > .ant-table-cell-fix-right {
    background: #fafafa !important;
  }
`;

const AllAnnouncement = ({ data = [], loading }) => {
  const [form] = Form.useForm();
  const containerRef = useRef(null);
  const history = useHistory();

  const durationType = Form.useWatch("duration_type", form);
  const pageTitleRef = useRef(null);

  const [scrollY, setScrollY] = useState(120);
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [importantOnly, setImportantOnly] = useState(false);

  const userinfo = useSelector(getUserInfo);
  const userList = useSelector(getUserList);

  useEffect(() => {
    if (!pageTitleRef.current) return;

    const baseOffset = 300;
    const minHeight = 120;

    const calculateScrollY = () => {
      const headerHeight = pageTitleRef.current.offsetHeight;
      const availableHeight = window.innerHeight - headerHeight - baseOffset;
      setScrollY(Math.max(minHeight, availableHeight));
    };

    calculateScrollY();

    const resizeObserver = new ResizeObserver(() => {
      calculateScrollY();
    });

    resizeObserver.observe(pageTitleRef.current);
    window.addEventListener("resize", calculateScrollY);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", calculateScrollY);
    };
  }, []);

  const dataSource = data
    ?.filter((item) => {
      if (String(item?.createdby) === String(userinfo?.userid)) return false;
      let announcementRoleIds = item?.roleid || item?.roleId || [];
      if (typeof announcementRoleIds === "string") {
        try {
          announcementRoleIds = JSON.parse(announcementRoleIds);
        } catch (e) {
          announcementRoleIds = [];
        }
      }
      if (
        Array.isArray(announcementRoleIds) &&
        announcementRoleIds?.length > 0
      ) {
        const userRoles = userinfo?.role_id || [];
        const hasAccess = userRoles.some((userRole) =>
          announcementRoleIds.map(String).includes(String(userRole))
        );
        if (!hasAccess) return false;
      }
      let announcementLocationIds = item?.locationid || item?.locationId || [];
      if (typeof announcementLocationIds === "string") {
        try {
          announcementLocationIds = JSON.parse(announcementLocationIds);
        } catch (e) {
          announcementLocationIds = [];
        }
      }
      if (
        Array.isArray(announcementLocationIds) &&
        announcementLocationIds.length > 0
      ) {
        const userBaseLocationId = userList?.find(
          (u) => String(u.userid) === String(userinfo?.userid)
        )?.baselocationid;
        const normalizedUserLocationId = Array.isArray(userBaseLocationId)
          ? userBaseLocationId.map(String)
          : [String(userBaseLocationId || "")];
        const hasLocationAccess = announcementLocationIds
          .map(String)
          .some((id) => normalizedUserLocationId.includes(id));
        if (!hasLocationAccess) return false;
      }
      let announcementDepartmentIds = item?.departmentid || [];
      if (typeof announcementDepartmentIds === "string") {
        try {
          announcementDepartmentIds = JSON.parse(announcementDepartmentIds);
        } catch (e) {
          announcementDepartmentIds = [];
        }
      }
      if (
        Array.isArray(announcementDepartmentIds) &&
        announcementDepartmentIds.length > 0
      ) {
        const userBaseDepartmentId = userList?.find(
          (u) => String(u.userid) === String(userinfo?.userid)
        )?.basedepartmentid;
        const normalizedUserDepartmentId = Array.isArray(userBaseDepartmentId)
          ? userBaseDepartmentId.map(String)
          : [String(userBaseDepartmentId || "")];
        const hasDepartmentAccess = announcementDepartmentIds
          .map(String)
          .some((id) => normalizedUserDepartmentId.includes(id));
        if (!hasDepartmentAccess) return false;
      }
      const matchImportant = importantOnly ? item?.level === "Important" : true;
      const matchSearch = searchText
        ? item?.title?.toLowerCase().includes(searchText.toLowerCase()) ||
          item?.createdby?.toLowerCase().includes(searchText.toLowerCase())
        : true;
      const matchDate = dateRange[0]
        ? dayjs(item?.createdat).isSame(dayjs(dateRange[0]), "day")
        : true;
      return matchImportant && matchSearch && matchDate;
    })
    ?.map((item) => ({
      key: item?.id,
      _raw: item,
      title: item?.title,
      level: item?.level,
      publishedby:
        userList?.find((u) => String(u.userid) === String(item?.createdby))
          ?.fullname ||
        item?.createdby ||
        "—",
      date: (() => {
        const d = item?.updatedat || item?.createdat;
        return d ? dayjs(d).format("DD MMM YYYY; hh:mm A") : "-";
      })(),
    }));

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      ellipsis: { showTitle: true },
      width: 300,
      render: (text) => (
        <span
          style={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: 280,
          }}
          title={text}
        >
          {text}
        </span>
      ),
    },
    { title: "Published By", dataIndex: "publishedby", key: "publishedby" },
    {
      title: "Date & Time",
      dataIndex: "date",
      key: "date",
    },
    {
      key: "actions",
      width: 150,
      fixed: "right",
      render: (_, record) => {
        const isImportant = record?.level === "Important";
        return (
          <ActionWrapper>
            {isImportant ? (
              <Popover
                content="This is an important announcement and cannot be removed from the list."
                overlayStyle={{ maxWidth: 220 }}
                placement="left"
              >
                <Info
                  sx={{
                    color: "#2D53DA",
                    fontSize: "20px",
                    marginRight: "8px",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    history.push(`/announcement-details/${record.key}`, {
                      viewOnly: true,
                    })
                  }
                />
              </Popover>
            ) : (
              <RemoveBtn
                onClick={() =>
                  history.push(`/announcement-details/${record.key}`)
                }
              >
                <CloseOutlined style={{ fontSize: 11 }} /> Remove
              </RemoveBtn>
            )}
          </ActionWrapper>
        );
      },
    },
  ];

  const validateDateRange = () => {
    const endDate = dayjs(form.getFieldValue("end_date"));
    const startDate = dayjs(form.getFieldValue("start_date"));
    if (dayjs(startDate).isAfter(endDate)) {
      form.setFieldsValue({
        end_date: startDate,
      });
    }
  };

  const validateEndDateRange = () => {
    const startDate = dayjs(form.getFieldValue("start_date"));
    const endDate = dayjs(form.getFieldValue("end_date"));
    if (dayjs(endDate).isBefore(startDate)) {
      form.setFieldsValue({
        start_date: endDate,
      });
    }
  };

  return (
    <AllAnnouncementWrap>
      <div className="page-title" ref={pageTitleRef}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            status: "waiting",
            duration_type: 4,
            month: dateInDetail(getKenyanDateTime()).monthName,
            status_id: "4",
            start_date: dayjs(),
            end_date: dayjs(),
          }}
        >
          <Row gutter={[12, 0]} align="middle" style={{ marginBottom: 16 }}>
            <Col>
              <Input
                prefix={
                  <SearchOutlined style={{ color: "#9ca3af", fontSize: 13 }} />
                }
                placeholder="Search"
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ borderRadius: 999, height: 36, width: 200 }}
              />
            </Col>

            <Col ref={containerRef}>
              <DatePicker
                format="DD-MM-YYYY"
                allowClear
                placeholder="Date"
                getPopupContainer={() => containerRef.current}
                onChange={(date) =>
                  setDateRange(date ? [date, date] : [null, null])
                }
                style={{ borderRadius: 999, height: 36, width: 200 }}
              />
            </Col>

            {/* Spacer */}
            <Col flex="auto" />

            {/* Important switch */}
            <Col
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                whiteSpace: "nowrap",
              }}
            >
              <span style={{ fontSize: 14, color: "#374151" }}>Important</span>
              <Switch
                checked={importantOnly}
                onChange={(checked) => setImportantOnly(checked)}
                style={{
                  backgroundColor: importantOnly ? "#6ee7b7" : undefined,
                }}
              />
            </Col>
          </Row>
        </Form>
      </div>

      <StyledTable
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        onRow={(record) => ({
          onClick: () => {
            const isImportant = record?.level === "Important";
            if (isImportant) {
              history.push(`/announcement-details/${record.key}`, {
                viewOnly: true,
              });
            } else {
              history.push(`/announcement-details/${record.key}`);
            }
          },
          style: { cursor: "pointer" },
        })}
        loading={loading}
        scroll={{ y: scrollY }}
        style={{ minHeight: scrollY + 50 }}
        rowClassName={(_, index) => (index % 2 ? "non-white" : "")}
      />
    </AllAnnouncementWrap>
  );
};

export default AllAnnouncement;
